import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type MatchStatus = 'SCHEDULED' | 'LIVE' | 'FINISHED' | 'POSTPONED' | 'CANCELLED';
export type MatchPhase =
  | 'PRE'
  | 'FIRST_HALF'
  | 'HT'
  | 'SECOND_HALF'
  | 'FT'
  | 'ET1'
  | 'ET_HT'
  | 'ET2'
  | 'PEN'
  | 'FINISHED';
export type MatchEventType =
  | 'GOAL'
  | 'YELLOW_CARD'
  | 'RED_CARD'
  | 'SUBSTITUTION'
  | 'PENALTY_SCORED'
  | 'PENALTY_MISSED'
  | 'OWN_GOAL'
  | 'VAR_DECISION';

export interface PresentingPartnerDto {
  id: string;
  name: string;
  logoUrl: string;
  websiteUrl: string | null;
}

export interface MatchClockDto {
  phase: MatchPhase;
  liveStartedAt: string | null;
  pausedAt: string | null;
  pausedAccumMs: number;
  serverNow: string;
  elapsedSec: number | null;
}

export interface MatchSummaryDto {
  id: string;
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo: string | null;
  awayTeamLogo: string | null;
  isHome: boolean;
  competition: string;
  matchday: number | null;
  date: string;
  venue: string;
  ticketUrl: string | null;
  status: MatchStatus;
  homeScore: number | null;
  awayScore: number | null;
  minute: number | null;
  presentingPartner: PresentingPartnerDto | null;
  clock: MatchClockDto;
  pushOnGoal: boolean;
}

export interface MatchEventDto {
  id: string;
  type: MatchEventType;
  minute: number;
  team: 'home' | 'away';
  playerName: string;
  detail: string | null;
  createdAt: string;
}

export interface MatchDetailDto extends MatchSummaryDto {
  lineupHome: unknown;
  lineupAway: unknown;
  stats: unknown;
  events: MatchEventDto[];
}

export interface CreateMatchInput {
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  isHome: boolean;
  competition: string;
  matchday?: number;
  date: string;
  venue: string;
  ticketUrl?: string;
  presentingPartnerId?: string;
}

export interface UpdateMatchInput {
  status?: MatchStatus;
  homeScore?: number;
  awayScore?: number;
  minute?: number;
  date?: string;
  venue?: string;
  ticketUrl?: string | null;
  homeTeamName?: string;
  awayTeamName?: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  isHome?: boolean;
  competition?: string;
  matchday?: number;
  lineupHome?: string[];
  lineupAway?: string[];
  stats?: Record<string, unknown>;
  presentingPartnerId?: string | null;
  pushOnGoal?: boolean;
}

export interface CreateMatchEventInput {
  type: MatchEventType;
  minute: number;
  team: 'home' | 'away';
  playerName: string;
  detail?: string;
}

export interface MatchCommentaryDto {
  id: string;
  text: string;
  createdAt: string;
}

export interface PaginatedMatches {
  data: MatchSummaryDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface MatchesFilters {
  page?: number;
  limit?: number;
  status?: MatchStatus;
  competition?: string;
  // T-93 — vista calendario: false = prossime (non concluse), true = concluse.
  concluded?: boolean;
}

const QK = {
  list: (filters: MatchesFilters) => ['matches', filters] as const,
  one: (id: string) => ['matches', id] as const,
  next: () => ['matches', 'next'] as const,
  commentary: (id: string) => ['matches', id, 'commentary'] as const,
};

export function useMatches(filters: MatchesFilters = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {};
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.status) params.status = filters.status;
      if (filters.competition) params.competition = filters.competition;
      if (filters.concluded !== undefined) params.concluded = filters.concluded;
      const res = await apiClient.get<PaginatedMatches>('/matches', { params });
      return res.data;
    },
  });
}

export function useMatch(id: string | null) {
  return useQuery({
    queryKey: QK.one(id ?? ''),
    enabled: id !== null,
    queryFn: async () => {
      const res = await apiClient.get<MatchDetailDto>(`/matches/${id}`);
      return res.data;
    },
  });
}

/**
 * Prossima partita (LIVE in corso, altrimenti prima SCHEDULED futura).
 * Usata nel backoffice per evidenziare in cima all'elenco la partita su cui
 * l'operatore deve agire, dato che la tabella è ordinata per data decrescente.
 */
export function useNextMatch() {
  return useQuery({
    queryKey: QK.next(),
    queryFn: async () => {
      const res = await apiClient.get<MatchSummaryDto | null>('/matches/next');
      return res.data;
    },
  });
}

export function useCreateMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateMatchInput) => {
      const res = await apiClient.post<MatchSummaryDto>('/matches', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['matches'] }),
  });
}

export function useUpdateMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateMatchInput }) => {
      const res = await apiClient.patch<MatchSummaryDto>(`/matches/${id}`, input);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['matches'] });
      qc.invalidateQueries({ queryKey: QK.one(vars.id) });
    },
  });
}

export function useDeleteMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/matches/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['matches'] }),
  });
}

export function useAddMatchEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: CreateMatchEventInput }) => {
      const res = await apiClient.post<MatchEventDto>(`/matches/${id}/events`, input);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: QK.one(vars.id) });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

export function useDeleteMatchEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ matchId, eventId }: { matchId: string; eventId: string }) => {
      await apiClient.delete(`/matches/${matchId}/events/${eventId}`);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: QK.one(vars.matchId) });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

export function useMatchCommentary(matchId: string | null) {
  return useQuery({
    queryKey: QK.commentary(matchId ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<{ data: MatchCommentaryDto[] }>(
        `/matches/${matchId}/commentary?limit=50`,
      );
      return res.data.data;
    },
    enabled: matchId != null,
  });
}

export function useAddMatchCommentary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ matchId, text }: { matchId: string; text: string }) => {
      const res = await apiClient.post<MatchCommentaryDto>(`/matches/${matchId}/commentary`, { text });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: QK.commentary(vars.matchId) });
    },
  });
}

// ───────────────────────────────────── LIVE controls

function useLiveAction(path: 'start' | 'pause' | 'resume') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (matchId: string) => {
      const res = await apiClient.post<MatchSummaryDto>(`/matches/${matchId}/clock/${path}`);
      return res.data;
    },
    onSuccess: (_d, matchId) => {
      qc.invalidateQueries({ queryKey: QK.one(matchId) });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

export const useStartClock = () => useLiveAction('start');
export const usePauseClock = () => useLiveAction('pause');
export const useResumeClock = () => useLiveAction('resume');

export function useSetPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ matchId, phase }: { matchId: string; phase: MatchPhase }) => {
      const res = await apiClient.post<MatchSummaryDto>(`/matches/${matchId}/phase`, { phase });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: QK.one(vars.matchId) });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

export function useSetScore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      matchId,
      homeScore,
      awayScore,
    }: {
      matchId: string;
      homeScore: number;
      awayScore: number;
    }) => {
      const res = await apiClient.post<MatchSummaryDto>(`/matches/${matchId}/score`, {
        homeScore,
        awayScore,
      });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: QK.one(vars.matchId) });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

/** T-208 — attiva/disattiva la pre-partita prima del calcio d'inizio. */
export function useSetPreMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ matchId, active }: { matchId: string; active: boolean }) => {
      const res = await apiClient.post<MatchSummaryDto>(`/matches/${matchId}/prematch`, { active });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: QK.one(vars.matchId) });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

/** T-216 — correzione manuale del cronometro: imposta i secondi di gioco. */
export function useSetClock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ matchId, elapsedSeconds }: { matchId: string; elapsedSeconds: number }) => {
      const res = await apiClient.post<MatchSummaryDto>(`/matches/${matchId}/clock/set`, {
        elapsedSeconds,
      });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: QK.one(vars.matchId) });
      qc.invalidateQueries({ queryKey: ['matches'] });
    },
  });
}

export interface TicketClickReportRow {
  matchId: string;
  homeTeamName: string;
  awayTeamName: string;
  date: string;
  ticketUrl: string;
  totalClicks: number;
}

export function useTicketClicksReport() {
  return useQuery({
    queryKey: ['matches', 'ticket-clicks-report'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: TicketClickReportRow[] }>(
        '/matches/ticket-clicks/report',
      );
      return res.data.data;
    },
  });
}
